import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const PLANS = [
    {
        name: "BookBlend Plus",
        price: "$9.99",
        cadence: "per month",
        tagline: "Everything you need to read together — clubs, chats, challenges.",
        features: [
            "Access to all Chat Rooms",
            "Join unlimited Book Clubs",
            "Weekly Discussion Questions",
            "Chapter-by-chapter Journaling",
            "Reading challenges, bingo & badges",
            "Reading analytics & Reading Wrapped",
            "Free 7-day trial — cancel or pause anytime",
        ],
        cta: "Start free trial",
        href: "/membership?tier=plus",
        highlight: false,
        testId: "pricing-plus",
    },
    {
        name: "BookBlend Pro",
        price: "$14.99",
        cadence: "per month",
        tagline: "For the readers building a bookish brand.",
        features: [
            "Everything in BookBlend Plus",
            "Social Media Manager access",
            "50 content credits per month, refilled automatically",
            "Content scheduling calendar",
            "Ready-made captions & hashtags",
            "Free 7-day trial — cancel or pause anytime",
        ],
        cta: "Start free trial",
        href: "/membership?tier=pro",
        highlight: true,
        badge: "Most Popular",
        testId: "pricing-pro",
    },
    {
        name: "The Bag Drop",
        pricePrefix: "from",
        price: "$34.99",
        cadence: "at the machine",
        tagline: "Grab-and-go reading joy, fresh from the machine.",
        features: [
            "The Page Turner 📖 — $34.99",
            "The Plot Twist ✨ — $39.99",
            "The Whole Story 🎨 — $49.99",
            "The Book & Brew ☕ — $59.99",
            "Every bag: a curated book + reading journal",
            "No ordering, no waiting — tap, grab, read",
        ],
        cta: "See what's inside",
        href: "/shop",
        highlight: false,
        testId: "pricing-bags",
    },
];

const PlanPrice = ({ plan }) => {
    const muted = plan.highlight ? "text-[#FDF6EE]/60" : "text-[#6B655C]";
    return (
        <div className="mt-6 flex items-baseline gap-2 flex-wrap">
            {plan.pricePrefix && (
                <span className={`text-sm font-medium ${muted}`}>
                    {plan.pricePrefix}
                </span>
            )}
            <span className="font-serif text-5xl md:text-6xl">
                {plan.price}
            </span>
            <span className={`text-sm whitespace-nowrap ${muted}`}>
                {plan.cadence}
            </span>
        </div>
    );
};

const PlanFeatureList = ({ plan }) => (
    <ul className="mt-8 space-y-3 flex-1">
        {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-[15px]">
                <span
                    className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        plan.highlight
                            ? "bg-[#C4956A]"
                            : "bg-[#C4956A]/15 text-[#C4956A]"
                    }`}
                >
                    <Check
                        size={12}
                        strokeWidth={3}
                        className={plan.highlight ? "text-white" : ""}
                    />
                </span>
                <span
                    className={
                        plan.highlight
                            ? "text-[#FDF6EE]/90"
                            : "text-[#2D2D2D]"
                    }
                >
                    {f}
                </span>
            </li>
        ))}
    </ul>
);

const PricingCard = ({ plan }) => (
    <div
        data-testid={plan.testId}
        className={`relative rounded-[2rem] p-8 md:p-10 flex flex-col ${
            plan.highlight
                ? "bg-[#2D2D2D] text-[#FDF6EE] border border-[#2D2D2D] shadow-lift md:-translate-y-4"
                : "bg-white border border-[#E5D7C5] text-[#2D2D2D] shadow-soft"
        } transition-all duration-300 hover:shadow-lift`}
    >
        {plan.badge && (
            <span className="absolute -top-3 right-6 inline-flex items-center px-3 py-1 rounded-full bg-[#E86A58] text-white text-xs font-semibold tracking-wider uppercase">
                {plan.badge}
            </span>
        )}
        <h3
            className={`font-serif text-3xl ${
                plan.highlight ? "text-white" : "text-[#2D2D2D]"
            }`}
        >
            {plan.name}
        </h3>
        <p
            className={`mt-2 text-sm ${
                plan.highlight ? "text-[#FDF6EE]/70" : "text-[#6B655C]"
            }`}
        >
            {plan.tagline}
        </p>
        <PlanPrice plan={plan} />
        <PlanFeatureList plan={plan} />
        <Link
            to={plan.href}
            data-testid={`${plan.testId}-cta`}
            className={`mt-10 inline-flex items-center justify-center px-6 py-3.5 rounded-full font-medium transition-all duration-300 ${
                plan.highlight
                    ? "bg-[#C4956A] text-white hover:bg-[#A67C56]"
                    : "bg-[#2D2D2D] text-[#FDF6EE] hover:bg-[#C4956A]"
            }`}
        >
            {plan.cta}
        </Link>
    </div>
);

export const Pricing = () => (
    <section
        id="pricing"
        data-testid="pricing-section"
        className="py-24 md:py-32"
    >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
            <div className="text-center max-w-2xl mx-auto mb-16">
                <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C4956A]">
                    Pricing
                </span>
                <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-3">
                    Pick your <span className="italic">membership</span>.
                </h2>
                <p className="mt-5 text-[#6B655C] text-lg">
                    Two simple tiers, one free week to fall in love. And
                    when you spot a machine — treat yourself.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                {PLANS.map((plan) => (
                    <PricingCard key={plan.name} plan={plan} />
                ))}
            </div>
        </div>
    </section>
);
